import React, { useState, useEffect } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(0);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      easing: 'ease-out',
    });

    // Fetch FAQs from API
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://zeroplushub.sparkx-ai.shop/api/faqs');

      if (!response.ok) {
        throw new Error('Failed to fetch FAQs');
      }

      const data = await response.json();

      if (data.success && data.faqs && Array.isArray(data.faqs) && data.faqs.length > 0) {
        setFaqs(data.faqs);
      } else {
        setFaqs([]);
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching FAQs:', err);
      setError('FAQ তথ্য লোড করতে সমস্যা হয়েছে। পরে আবার চেষ্টা করুন।');
      setFaqs([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="bg-white max-w-5xl mx-auto border border-gray-300 rounded-lg p-6 my-6">
      <div className="max-w-4xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-12" data-aos="fade-down">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h1>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-600 mx-auto mb-6 rounded-full"></div>
          <p className="text-base text-gray-600 leading-relaxed max-w-3xl mx-auto">
            Welcome to our FAQ section! Here you will find answers to common questions. We have tried to answer all the questions you may have about our products, ordering process, shipping, returns and other important topics.
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col justify-center items-center py-16">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-pulse h-8 w-8 bg-blue-300 rounded-full"></div>
              </div>
            </div>
            <p className="mt-6 text-gray-600 font-medium animate-pulse">Loading FAQs...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div
            className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg shadow-sm mb-8 transform transition-all duration-300 hover:shadow-md"
            data-aos="fade-up"
          >
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-semibold text-red-800 mb-1">Error</p>
                <p className="text-sm text-red-700">{error}</p>
                <button
                  onClick={fetchFAQs}
                  className="mt-3 text-sm font-medium text-red-600 hover:text-red-800 underline transition-colors duration-200"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* FAQ List */}
        {!loading && !error && faqs.length > 0 && (
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={faq.id || index}
                className="group bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden"
                data-aos="fade-up"
                data-aos-delay={index * 50}
              >
                {/* Question */}
                <button
                  className="w-full flex justify-between items-center p-5 md:p-6 text-left hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 transition-all duration-300"
                  onClick={() => toggleFAQ(index)}
                  aria-expanded={index === openIndex}
                >
                  <div className="flex items-start flex-1 pr-4">
                    <span className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm mr-4 shadow-md">
                      {index + 1}
                    </span>
                    <h3 className="text-base md:text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors duration-300">
                      {faq.question}
                    </h3>
                  </div>
                  <div className="flex-shrink-0 ml-2">
                    <div className={`w-8 h-8 rounded-full bg-gray-100 group-hover:bg-blue-100 flex items-center justify-center transition-all duration-300 ${index === openIndex ? 'rotate-180 bg-blue-500 group-hover:bg-blue-600' : ''
                      }`}>
                      <svg
                        className={`w-5 h-5 transition-colors duration-300 ${index === openIndex ? 'text-white' : 'text-gray-600 group-hover:text-blue-600'
                          }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2.5}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>
                </button>

                {/* Answer */}
                <div
                  className={`overflow-hidden transition-all duration-500 ease-in-out ${index === openIndex ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                >
                  <div className="px-5 md:px-6 pb-5 md:pb-6 pt-2">
                    <div className="pl-12 border-l-4 border-blue-200 ml-0 md:ml-0">
                      <div className="pl-4 text-sm md:text-base text-gray-700 leading-relaxed">
                        {faq.answer}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && faqs.length === 0 && (
          <div
            className="bg-white border-2 border-dashed border-gray-300 p-12 rounded-xl text-center"
            data-aos="fade-up"
          >
            <div className="flex flex-col items-center">
              <svg className="w-24 h-24 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-lg font-medium text-gray-900 mb-2">No FAQs Available</p>
              <p className="text-gray-600 mb-4">
                এই মুহূর্তে কোনো FAQ পাওয়া যায়নি। পরে আবার চেষ্টা করুন।
              </p>
              <button
                onClick={fetchFAQs}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200 shadow-md hover:shadow-lg"
              >
                Refresh
              </button>
            </div>
          </div>
        )}

        {/* Footer Note */}
        {!loading && faqs.length > 0 && (
          <div
            className="mt-12 bg-gradient-to-r from-blue-50 to-purple-50 border-l-4 border-blue-500 p-6 md:p-8 rounded-xl shadow-md"
            data-aos="fade-up"
            data-aos-delay="200"
          >
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="w-8 h-8 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-base md:text-lg font-bold text-gray-900 mb-2">
                  Still have questions?
                </p>
                <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                  Our customer service team is here to help. Contact us through the information provided on our Contact page or reach out via email.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FAQPage;